import { useMemo } from 'react'
import { ArrowDown, ArrowUp, Eye } from 'lucide-react'
import { useDebounce } from '@uidotdev/usehooks'

import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { formatCurrency } from '@/lib/utils'
import { usePriceAdjustmentPreview } from '@/lib/services/price-adjustments/priceAdjustments.query'
import {
  normalizePriceAdjustmentPayload,
  PRICE_ADJUSTMENT_SCOPE_COLLECTIONS,
} from '@/lib/services/price-adjustments/priceAdjustments.schema'
import {
  getPriceAdjustmentCardTitle,
  getPriceAdjustmentSignedAmountLabel,
  getPriceAdjustmentSupportingTitle,
} from '@/lib/services/price-adjustments/priceAdjustments.summary'

import { PriceChangeAlert } from '@/components/products/price-changes/PriceChangeAlert'

const getPriceAdjustmentPreviewPayload = (value) => {
  const normalized = normalizePriceAdjustmentPayload(value)

  return {
    scope_type: normalized.scope_type,
    collection_ids: normalized.collection_ids,
    rounding_mode: normalized.rounding_mode,
    adjustment_type: normalized.adjustment_type,
    operation: normalized.operation,
    amount: normalized.amount,
  }
}

const canPreviewPriceAdjustmentPayload = (payload) => {
  if (
    !payload.scope_type ||
    !payload.rounding_mode ||
    !payload.adjustment_type ||
    !payload.operation ||
    typeof payload.amount !== 'number' ||
    payload.amount <= 0
  ) {
    return false
  }

  if (payload.scope_type === PRICE_ADJUSTMENT_SCOPE_COLLECTIONS) {
    return payload.collection_ids.length > 0
  }

  return true
}

export function PriceAdjustmentScopeChangePreview({ value, selectableCollections }) {
  const { adjustment_type, amount, operation, rounding_mode, scope_type } = value
  const collectionIdsKey = (value.collection_ids || [])
    .map((collectionId) => String(collectionId))
    .join('|')

  // Keep the preview query key stable until the debounced draft actually changes.
  const previewPayload = useMemo(() => {
    const payload = getPriceAdjustmentPreviewPayload({
      adjustment_type,
      amount,
      collection_ids: collectionIdsKey ? collectionIdsKey.split('|') : [],
      operation,
      rounding_mode,
      scope_type,
    })

    return canPreviewPriceAdjustmentPayload(payload) ? payload : null
  }, [adjustment_type, amount, collectionIdsKey, operation, rounding_mode, scope_type])
  const canPreview = Boolean(previewPayload)
  const debouncedPreviewPayload = useDebounce(previewPayload, 250)

  const previewQuery = usePriceAdjustmentPreview(debouncedPreviewPayload, {
    enabled: Boolean(debouncedPreviewPayload),
    staleTime: 30_000,
  })

  if (!canPreview) {
    return null
  }

  const selectedCollectionIds = (value.collection_ids || []).map((collectionId) =>
    String(collectionId)
  )
  const selectedCollections = selectableCollections.filter((collection) =>
    selectedCollectionIds.includes(String(collection.id))
  )
  const summaryValue = {
    ...value,
    collections: selectedCollections,
  }
  const preview = previewQuery.data?.preview ?? null
  const isDecrease = summaryValue?.operation === 'decrease'
  const DirectionIcon = isDecrease ? ArrowDown : ArrowUp
  const cardTitle = getPriceAdjustmentCardTitle(summaryValue)
  const supportingTitle = getPriceAdjustmentSupportingTitle(summaryValue)
  const exampleProductLabel = [preview?.product?.name, preview?.variant?.name]
    .filter(Boolean)
    .join(' · ')

  if (!previewQuery.isFetching && !previewQuery.isError && !preview) {
    return null
  }

  return (
    <PriceChangeAlert
      title="Preview price change"
      icon={<Eye />}
      tone="neutral"
      body={
        preview ? (
          <Card surface="subtle" className="overflow-hidden py-0">
            <CardContent className="grid gap-3 p-5 lg:grid-cols-2">
              <div className="rounded-xl bg-background/75 p-4 ring-1 ring-inset ring-border/25 dark:bg-background/25 dark:ring-white/10">
                <Typography variant="eyebrow" className="block">
                  Current to adjusted
                </Typography>
                <Typography variant="h3" wrap="balance" className="mt-2 sm:text-3xl">
                  {formatCurrency(preview.base_price)} {'\u2192'}{' '}
                  {formatCurrency(preview.adjusted_price)}
                </Typography>
                <div className="mt-3 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4 lg:block lg:space-y-1">
                  <Typography variant="eyebrow" className="block">
                    Example affected product
                  </Typography>
                  <Typography variant="label" className="block sm:text-right lg:text-left">
                    {exampleProductLabel}
                  </Typography>
                </div>
              </div>

              <div className="rounded-xl bg-background/75 p-4 ring-1 ring-inset ring-border/25 dark:bg-background/25 dark:ring-white/10">
                <div className="space-y-1">
                  <Typography variant="eyebrow" className="block">
                    Applied to
                  </Typography>
                  <Typography variant="cardTitle" className="block">
                    {cardTitle}
                  </Typography>
                  {supportingTitle ? (
                    <Typography variant="caption" className="block">
                      {supportingTitle}
                    </Typography>
                  ) : null}
                </div>

                <div className="mt-4 border-t border-border/30 pt-4">
                  <Typography variant="eyebrow" className="block">
                    Price Change
                  </Typography>
                  <Typography
                    variant="label"
                    tone={isDecrease ? 'interaction' : 'success'}
                    weight="semibold"
                    className="mt-2 inline-flex items-center gap-1"
                  >
                    <DirectionIcon className="size-3.5 shrink-0" aria-hidden="true" />
                    {getPriceAdjustmentSignedAmountLabel(summaryValue)}
                  </Typography>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null
      }
    >
      {previewQuery.isFetching ? (
        <Typography variant="label" tone="inherit" className="block">
          {preview ? 'Updating preview…' : 'Finding an example product…'}
        </Typography>
      ) : previewQuery.isError ? (
        <Typography variant="label" tone="inherit" className="block">
          Unable to preview this price change right now.
        </Typography>
      ) : (
        <Typography variant="label" tone="inherit" className="block">
          This is how the price change will read once it is saved.
        </Typography>
      )}
    </PriceChangeAlert>
  )
}
